import React, { useRef } from 'react';
import { Feature } from '.generated/templates/Feature.Items.model';
import { Sitecore } from '.generated/templates/_.Sitecore.Override';
import classNames from 'classnames';
import ImageWrapper, { resizeHeight } from 'components/helpers/ImageWrapper/ImageWrapper';
import { RichText, Text, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import Heading from 'components/helpers/Heading/Heading';
import { DevLog } from 'lib/utils/logging-utils';
import useLocalTimeZone from 'lib/hooks/useLocalTimeZone';
import { DateTime } from 'luxon';
import { useReactToPrint } from 'react-to-print';
import { PrinterIcon } from '@heroicons/react/24/outline';
import { PriorAuthInputs } from './PriorAuthLookup';
import ExternalSvgIcon from 'components/helpers/Icons/ExternalSvgIcon/ExternalSvgIcon';
import AZBlueLogo from 'public/BCBS_AZ_ Icons/AZ_Blue_Logo.svg';

export type ResultMessages = Feature.Data.Priorauth.ResultMessages.Fields.ResultMessages &
  Sitecore.Override.ItemEx[];

export type PriorAuthLookupAuthTrueResultsProps = Sitecore.Override.ComponentBase &
  Feature.Components.PriorAuthLookup.Fields.PriorAuthLookup &
  ResultMessages &
  PriorAuthInputs &
  any;

const PriorAuthLookupAuthTrueResults = ({
  resultMessage,
  authFormData,
  priorAuthInputs,
}: PriorAuthLookupAuthTrueResultsProps) => {
  DevLog('PriorAuthLookupAuthFalseResults resultMessage = ', resultMessage);
  DevLog('PriorAuthLookupAuthFalseResults authFormData = ', authFormData);
  DevLog('PriorAuthLookupAuthFalseResults fields = ', resultMessage?.fields);

  const defaultResultsTitle = resultMessage?.fields?.titleLabel;
  const iconImageSrc = resultMessage?.fields?.iconImage?.value?.src;
  const timeZoneName = useLocalTimeZone();

  const contentRef = useRef<HTMLDivElement>(null);
  const reactToPrintFn = useReactToPrint({
    documentTitle: `Prior Authorization Lookup`,
    contentRef,
  });

  const { sitecoreContext } = useSitecoreContext();

  return (
    <div
      ref={contentRef}
      // DO NOT REMOVE ID!
      // https://github.com/MatthewHerbst/react-to-print/pull/785]
      // Add this ID here since we use "important: '#azblue-body'" in our tailwind config.
      id="azblue-body"
    >
      {/* Print header LOGO */}
      <ExternalSvgIcon
        field={resizeHeight({ value: AZBlueLogo }, 56)}
        removeFill={false}
        className="hidden print:block print:my-6"
      />

      <div
        className={classNames(
          'flex flex-col space-y-6 text-left pb-12 pt-2 md:px-12',
          'print:space-y-4 print:p-0 print-links'
        )}
      >
        <div className={classNames('flex items-center space-x-4')}>
          {/* Find proper logo or maybe this is the print logo */}
          <ImageWrapper
            field={{
              value: {
                src: iconImageSrc,
                width: '32px',
                height: '28px',
              },
            }}
          />
          <Heading
            headingText={defaultResultsTitle}
            headingClassName={classNames('font-family text-black')}
            headingLevel="h2"
            headingSize="sm"
            descriptionClassName="mb-8"
            headingIsRichText
          />
        </div>

        {/*Date Time & Print Button*/}
        <div className={classNames('!mt-2 flex items-center text-grey text-sm gap-4')}>
          <p>
            As of: {DateTime.now().toLocaleString(DateTime.TIME_SIMPLE)}&nbsp;
            <span className="no-print">{timeZoneName}</span>,&nbsp;
            {DateTime.now().toLocaleString(DateTime.DATE_SHORT)}
          </p>
          <button
            id="prior-auth-print-button"
            onClick={() => {
              reactToPrintFn();
            }}
            className="flex items-center text-sm text-center text-blue print:hidden"
          >
            <PrinterIcon className="w-6 h-6 mr-1 stroke-blue" />
            Print
          </button>
        </div>

        {/* Special message for Medicare Advantage members */}
        {authFormData?.GroupNumber === 'H0302' &&
          resultMessage?.fields?.medicareAdvantageDescription && (
            <div className={classNames('text-black')}>
              <RichText
                field={{ value: resultMessage?.fields?.medicareAdvantageDescription?.value }}
                editable={false}
              />
            </div>
          )}

        <div>
          <p className={classNames('text-black')}>
            <span className="font-bold">Date of Service or Procedure:</span>{' '}
            {priorAuthInputs?.DateOfService}
          </p>
          <p className={classNames('text-black')}>
            <span className="font-bold">Member ID:</span> {priorAuthInputs?.MemberId}
          </p>

          <p className={classNames('text-black')}>
            <span className="font-bold">Group Number:</span> {authFormData?.GroupNumber}
          </p>

          <p className={classNames('text-black')}>
            <span className="font-bold">Procedure Code:</span>{' '}
            {authFormData?.ProcedureCode?.CPTCode}
          </p>
        </div>

        <div className={classNames('text-black')}>
          <span className="font-bold">Description:</span> {authFormData?.ProcedureCode?.Description}
        </div>
        {/* Start of Gold Card section of the result message */}
        {authFormData.GoldCard.toLowerCase() === 'no' ? (
          <div className={classNames('text-black')}>
            <RichText
              field={{ value: resultMessage?.fields?.goldCardNoMessage?.value }}
              editable={false}
            />
          </div>
        ) : authFormData.GoldCard.toLowerCase() === 'yes' ? (
          <div className={classNames('text-black')}>
            <RichText
              field={{ value: resultMessage?.fields?.goldCardYesMessage?.value }}
              editable={false}
            />
          </div>
        ) : authFormData.GoldCard.toLowerCase() === 'see message' ? (
          <div className={classNames('text-black')}>
            <RichText
              field={{ value: resultMessage?.fields?.goldCardSeeMessage?.value }}
              editable={false}
            />
          </div>
        ) : null}
        {/* Start of Site of Service section of the result message */}
        {authFormData.SiteOfService.toLowerCase() === 'no' ? (
          <div className={classNames('text-black')}>
            <RichText
              field={{ value: resultMessage?.fields?.siteOfServiceNoMessage?.value }}
              editable={false}
            />
          </div>
        ) : authFormData.SiteOfService.toLowerCase() === 'yes' ? (
          <div className={classNames('text-black')}>
            <RichText
              field={{ value: resultMessage?.fields?.siteOfServiceYesMessage?.value }}
              editable={false}
            />
          </div>
        ) : authFormData.SiteOfService.toLowerCase() === 'see message' ? (
          <div className={classNames('text-black')}>
            <RichText
              field={{ value: resultMessage?.fields?.siteOfServiceSeeMessage?.value }}
              editable={false}
            />
          </div>
        ) : null}
        {/* Start of Authorization Manager section of the result message */}
        {authFormData.AuthManager.toLowerCase() === 'bcbsaz' ? (
          <div className={classNames('text-black')}>
            <RichText
              field={{ value: resultMessage?.fields?.authManagerBCBSAZMessage?.value }}
              editable={false}
            />
          </div>
        ) : authFormData.AuthManager.toLowerCase() === 'evicore' ? (
          <div className={classNames('text-black')}>
            <RichText
              field={{ value: resultMessage?.fields?.authManagerEvicoreMessage?.value }}
              editable={false}
            />
          </div>
        ) : authFormData.AuthManager.toLowerCase() === 'see message' ? (
          <div className={classNames('text-black')}>
            <RichText
              field={{ value: resultMessage?.fields?.authManagerSeeMessage?.value }}
              editable={false}
            />
          </div>
        ) : null}
        {/* Conditional/Optional Message Test from the Stargate API */}
        {authFormData?.MessageText && (
          <div className={classNames('text-black')}>
            <span className="font-bold">Message:</span>{' '}
            <RichText field={{ value: authFormData.MessageText }} editable={false} />
          </div>
        )}

        <div className={classNames('rte', 'text-sm', 'italic', 'text-black', 'print:not-italic')}>
          <RichText field={{ value: resultMessage?.fields?.messageText?.value }} editable={false} />
        </div>

        {/* Print footer */}
        <div className="hidden w-full print:flex">
          <Text
            className={classNames('lg:max-w-[380px]', 'lg:text-right', 'text-xs')}
            field={
              (
                sitecoreContext.route?.placeholders[
                  'BCBSAZ-Footer'
                ][0] as Feature.Components.Footer.Fields.Footer
              ).fields?.copyright
            }
            tag="p"
          />
        </div>
      </div>
    </div>
  );
};

export default PriorAuthLookupAuthTrueResults;
